import { useState } from "react";
import { Copy, Check, Terminal } from "lucide-react";
import { Button } from "@/components/ui/button";

export const CodePreview = () => {
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const codeExamples = [
    {
      label: "React",
      language: "tsx",
      code: `import { TechBus } from '@techbus/sdk';

// Initialize the client
const client = new TechBus({
  apiKey: process.env.TECHBUS_API_KEY,
});

// Deploy your application
async function deploy() {
  const deployment = await client.deploy({
    project: 'my-app',
    branch: 'main',
    env: 'production',
  });
  
  console.log('Deployed!', deployment.url);
}`,
    },
    {
      label: "Python",
      language: "python",
      code: `from techbus import TechBus

# Initialize the client
client = TechBus(
    api_key=os.environ["TECHBUS_API_KEY"]
)

# Deploy your application
deployment = client.deploy(
    project="my-app",
    branch="main",
    env="production"
)

print(f"Deployed! {deployment.url}")`,
    },
    {
      label: "CLI",
      language: "bash",
      code: `# Install the CLI
npm install -g @techbus/cli

# Login to your account
techbus login

# Initialize a new project
techbus init my-awesome-app

# Deploy to production
techbus deploy --prod

# View deployment logs
techbus logs --follow`,
    },
  ];

  const handleCopy = () => {
    navigator.clipboard.writeText(codeExamples[activeTab].code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section className="py-24 relative overflow-hidden px-4 sm:px-6 lg:px-8 xl:px-12">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-primary/5" />
      
      <div className="container mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass border border-primary/20 mb-6">
              <Terminal className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">Developer Experience</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
              Code that
              <span className="gradient-text"> just works.</span>
            </h2>
            
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
            We deliver clean, maintainable code that integrates with your stack in no time. Whether you're a startup or an enterprise, we focus on developer experience and modern architecture so your product is reliable and ready to scale.
            </p>

            <div className="flex flex-wrap gap-3">
              {["JavaScript", "TypeScript", "Python", "Go", "Rust", "Java","React","React Native","Next.js","Node.js","Express","MongoDB","PostgreSQL","MySQL","Firebase","AWS","Azure","Google Cloud","Docker","Kubernetes","Jenkins","Travis CI","CircleCI","GitLab CI","GitHub Actions","Bitbucket Pipelines","CodePipeline","CodeBuild","CodeDeploy","CodeCommit","CodeStar","CodePipeline","CodeBuild","CodeDeploy","CodeCommit","CodeStar"].map((lang) => (
                <span
                  key={lang}
                  className="px-4 py-2 rounded-lg bg-muted text-sm font-medium hover:bg-primary/10 hover:text-primary transition-colors cursor-pointer"
                >
                  {lang}
                </span>
              ))}
            </div>
          </div>

          {/* Right Code Block */}
          <div className="glass-card rounded-2xl overflow-hidden border border-border/50">
            {/* Tab Bar */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/50 bg-muted/30">
              <div className="flex gap-1">
                {codeExamples.map((example, index) => (
                  <button
                    key={example.label}
                    onClick={() => setActiveTab(index)}
                    className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === index
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    }`}
                  >
                    {example.label}
                  </button>
                ))}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopy}
                className="gap-2"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 text-green-500" />
                    <span className="text-green-500">Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    <span>Copy</span>
                  </>
                )}
              </Button>
            </div>

            {/* Code Content */}
            <div className="p-6 bg-background/50 overflow-x-auto">
              <pre className="text-sm font-mono leading-relaxed">
                <code className="text-foreground">
                  {codeExamples[activeTab].code.split('\n').map((line, i) => (
                    <div key={i} className="flex">
                      <span className="text-muted-foreground w-8 text-right mr-4 select-none">{i + 1}</span>
                      <span className="flex-1">
                        {line.includes('//') || line.includes('#') ? (
                          <span className="text-muted-foreground">{line}</span>
                        ) : line.includes("'") || line.includes('"') ? (
                          <span>
                            {line.split(/(['"][^'"]*['"])/).map((part, j) => (
                              <span key={j} className={part.startsWith("'") || part.startsWith('"') ? "text-green-400" : ""}>
                                {part}
                              </span>
                            ))}
                          </span>
                        ) : (
                          line
                        )}
                      </span>
                    </div>
                  ))}
                </code>
              </pre>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};